import { supabase } from '@/products/shared/services/supabase/client';
import { jwtDecode } from 'jwt-decode';

export const MFA_ENABLED_CONFIG_KEY = 'mfa_enabled';

export const normalizeTenantConfigBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  return null;
};

const coerceTenantId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/** Roles that bypass the tenant MFA requirement. */
export const MFA_EXEMPT_ROLES = ['super_admin'] as const;

export const isMfaExemptRole = (role: string | null | undefined): boolean =>
  typeof role === 'string' && MFA_EXEMPT_ROLES.includes(role as (typeof MFA_EXEMPT_ROLES)[number]);

export type SessionMfaClaims = {
  tenantId: number | null;
  userRole: string | null;
};

/** Resolve tenant_id and user_role from JWT, then fall back to user_roles. */
export const resolveSessionClaims = async (): Promise<SessionMfaClaims> => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    return { tenantId: null, userRole: null };
  }

  const token = sessionData.session.access_token;
  try {
    const decoded = jwtDecode(token) as { tenant_id?: unknown; user_role?: unknown };
    const fromJwt = coerceTenantId(decoded.tenant_id);
    const roleFromJwt = typeof decoded.user_role === 'string' ? decoded.user_role : null;
    if (fromJwt != null) {
      return { tenantId: fromJwt, userRole: roleFromJwt };
    }
  } catch {
    // Fall through to user_roles lookup.
  }

  const userId = sessionData.session.user.id;
  const { data, error } = await supabase
    .from('user_roles')
    .select('tenant_id, role')
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  return {
    tenantId: coerceTenantId(data?.tenant_id),
    userRole: typeof data?.role === 'string' ? data.role : null,
  };
};

export const resolveSessionTenantId = async (): Promise<number | null> =>
  (await resolveSessionClaims()).tenantId;

/** Default when unset: MFA is off for the tenant. */
export const fetchTenantMfaEnabled = async (tenantId: number | null): Promise<boolean> => {
  if (tenantId == null) {
    return false;
  }

  const { data, error } = await supabase.rpc('get_tenant_config', {
    p_tenant_id: tenantId,
    p_config_key: MFA_ENABLED_CONFIG_KEY,
    p_default_value: false,
  });

  if (error) {
    throw new Error(error.message);
  }

  return normalizeTenantConfigBoolean(data) ?? false;
};

/** Resolve tenant from the current session, then read mfa_enabled. */
export const fetchSessionTenantMfaEnabled = async (): Promise<boolean> => {
  const { tenantId } = await resolveSessionClaims();
  return fetchTenantMfaEnabled(tenantId);
};

/** Tenant MFA flag with role exemptions applied — use this to gate access. */
export const fetchSessionMfaRequired = async (): Promise<boolean> => {
  const { tenantId, userRole } = await resolveSessionClaims();
  if (isMfaExemptRole(userRole)) {
    return false;
  }
  return fetchTenantMfaEnabled(tenantId);
};

export const setTenantMfaEnabled = async (
  tenantId: number,
  enabled: boolean,
): Promise<void> => {
  const { error } = await supabase.rpc('set_tenant_config', {
    p_tenant_id: tenantId,
    p_config_key: MFA_ENABLED_CONFIG_KEY,
    p_config_value: enabled,
  });

  if (error) {
    throw new Error(error.message);
  }
};

/**
 * Paths users may visit while MFA is required but not yet enrolled. TOTP
 * enrollment happens on /mfa/verify, so no in-app page is reachable until a
 * factor is verified.
 */
export const MFA_ENROLLMENT_EXEMPT_PATHS = [
  '/mfa/verify',
  '/signin',
  '/forgot-password',
  '/update-password',
] as const;

export const isMfaEnrollmentExemptPath = (pathname: string): boolean =>
  MFA_ENROLLMENT_EXEMPT_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
